import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@pilot-hub/auth'
import {
  AppShell, NavIcons, NAV_ICON_CLASS, UserMenuIcons, useFeedbackUngelesen,
  ConnectionProvider, StatusBar, StaleBanner, useConnectionRetry,
  EinsatzProvider, EinsatzWahl, EinsatzHinweis,
} from '@pilot-hub/ui'
import type { NavItem, UserMenuEntry } from '@pilot-hub/ui'
import { DjiZuordnung } from './DjiZuordnung'
import { CloudAufforderungen } from './CloudAufforderungen'
import { FlugStatusChip } from './FlugStatusChip'
import { VorflugPopup } from './VorflugPopup'
import { FlugQueueHinweis, useFlugQueueAnzahl } from './FlugQueueHinweis'
import { api, missionApi, reportConnectionError } from '@pilot-hub/api-client'
import { Plane, UserCog, Check } from 'lucide-react'


type PilotMode = 'PILOT' | 'CO_PILOT'

const PAGE_TITLES: Record<string, string> = {
  '/telemetrie': 'Telemetrie', '/telemetrie-co': 'Telemetrie', '/taktik-karte': 'Taktik-Karte',
  '/live-video-co': 'Live-Video', '/luftraum-beobachtung': 'Luftraum-Beobachtung', '/einsatz-details': 'Einsatz-Details',
}

/**
 * Piloten-Modul auf der gemeinsamen AppShell (packages/ui/src/components/shell).
 * Pilot- und Co-Pilot-Modus haben eigene Menüsätze (pilotMode-Filter); das Dock deckt beide ab,
 * nicht passende Pfade werden von der Shell herausgefiltert.
 */
export function Layout() {
  const { user } = useAuth()
  const [pilotMode, setPilotMode] = useState<PilotMode>(() => {
    const saved = localStorage.getItem('pilot-hub-pilot-mode')
    return (saved as PilotMode) || 'PILOT'
  })
  const userId = user?.id

  // Pilot-Modus: Kandidaten = laufende Einsätze (wie "Flug erfassen"), Vorbelegung = Einsatz aus dem Kräfte Management.
  const ladePilot = useCallback(() => missionApi.getForFlight(), [])
  const vorschlagPilot = useCallback(async () => {
    if (!userId) return null
    const z = await missionApi.getMeinKraefteEinsatz(userId)
    return (z.data as { einsatz?: { id?: number } | null } | undefined)?.einsatz?.id ?? null
  }, [userId])
  // Co-Pilot-Modus: nur der Einsatz aus dem Kräfte Management, nicht wählbar (gesperrt).
  const ladeCoPilot = useCallback(() => (userId ? missionApi.getMeinKraefteEinsatz(userId) : Promise.resolve({ data: { einsatz: null } })), [userId])
  const vorschlagCoPilot = useCallback((liste: { id: number }[]) => liste[0]?.id ?? null, [])

  const istPilot = pilotMode === 'PILOT'
  return (
    <ConnectionProvider staleMs={60000}>
      {/* key: beim Moduswechsel neu aufbauen (eigener Speicher je Modus, andere Kandidaten, Sperre) */}
      <EinsatzProvider
        key={pilotMode}
        modul={istPilot ? 'pilot' : 'pilot-co'}
        userId={userId}
        loader={istPilot ? ladePilot : ladeCoPilot}
        suggest={istPilot ? vorschlagPilot : vorschlagCoPilot}
        locked={!istPilot}
        lockedHint="Im Co-Pilot-Modus gilt der Einsatz aus dem Kräfte Management. Die Wahl ändert der Pilot bzw. die Führung dort."
      >
        <LayoutInner pilotMode={pilotMode} setPilotMode={setPilotMode} />
      </EinsatzProvider>
    </ConnectionProvider>
  )
}

function LayoutInner({ pilotMode, setPilotMode }: { pilotMode: PilotMode; setPilotMode: (m: PilotMode) => void }) {
  const { user, logout } = useAuth()
  const queueAnzahl = useFlugQueueAnzahl()
  const location = useLocation()
  const feedbackUngelesen = useFeedbackUngelesen()
  const navigate = useNavigate()

  // Navigation beim Modus-Wechsel — nur wenn nicht bereits auf Ziel-Seite
  useEffect(() => {
    const currentPath = location.pathname
    if (pilotMode === 'CO_PILOT' && currentPath !== '/checklisten' && currentPath !== '/taktik-karte-co' && currentPath !== '/telemetrie-co' && currentPath !== '/cockpit-co' && currentPath !== '/missionsplaner-co' && currentPath !== '/live-video-co' && currentPath !== '/settings' && currentPath !== '/feedback') {
      navigate('/checklisten', { replace: true })
    } else if (pilotMode === 'PILOT' && currentPath !== '/flug-erfassen' && currentPath !== '/flugbuch' && currentPath !== '/settings' && currentPath !== '/feedback') {
      navigate('/flug-erfassen', { replace: true })
    }
  }, [pilotMode, navigate, location.pathname])

  // "Live"-Hinweis in der Navigation (Co-Pilot): grün, solange eine Drohne in der Luft ist und aktuelle Flugdaten sendet
  const [drohneLive, setDrohneLive] = useState(false)
  const pruefenLive = useCallback(() => {
    if (document.visibilityState === 'hidden') return
    api.get('/dji/live')
      .then(r => setDrohneLive((r.data as { in_der_luft: boolean; alter_s: number }[]).some(d => d.in_der_luft && d.alter_s <= 10)))
      .catch((err: any) => {
        // Fehler ist NICHT "Drohne nicht live": letzten bekannten Wert behalten. Netzwerk/5xx meldet der Interceptor schon,
        // Antworten mit 4xx (Berechtigung o. ä.) melden wir hier selbst an die Statusanzeige.
        const status = err?.response?.status as number | undefined
        if (status && status >= 400 && status < 500) {
          reportConnectionError(`Live-Status der Drohne nicht abrufbar (${status})`, { status, url: '/dji/live' })
        }
      })
  }, [])
  useEffect(() => {
    if (pilotMode !== 'CO_PILOT') return
    pruefenLive()
    const t = setInterval(pruefenLive, 5000)
    return () => clearInterval(t)
  }, [pilotMode, pruefenLive])
  useConnectionRetry(pruefenLive)

  // Persist pilotMode to localStorage
  useEffect(() => {
    document.documentElement.dataset.pilotRolle = pilotMode
    localStorage.setItem('pilot-hub-pilot-mode', pilotMode)
  }, [pilotMode])

  const navItems: NavItem[] = [
    { path: '/flug-erfassen', label: 'Flug erfassen', icon: <NavIcons.flightNew className={NAV_ICON_CLASS} />, roles: ['PILOT'], pilotMode: 'PILOT' },
    { path: '/flugbuch', label: 'Flugbuch', icon: <NavIcons.logbook className={NAV_ICON_CLASS} />, roles: ['PILOT'], pilotMode: 'PILOT' },
    // Co-Pilot Mode: Dashboard als eigener Navigationspunkt
    { path: '/checklisten', label: 'Dashboard', icon: <NavIcons.checklist className={NAV_ICON_CLASS} />, roles: ['PILOT'], requiresFunction: ['Co-Pilot'], pilotMode: 'CO_PILOT' },
    // Taktische Karte — im Co-Pilot Modus direkt unter Dashboard
    { path: '/taktik-karte-co', label: 'Taktik-Karte', icon: <NavIcons.map className={NAV_ICON_CLASS} />, roles: ['PILOT', 'LEITSTELLE'], requiresMission: true, pilotMode: 'CO_PILOT', live: drohneLive },
    // Cockpit: Bild der Fernsteuerung mit Einblendungen, Karte, Linse (später Kamera-/Flugbefehle) – nur Co-Pilot-Modus
    { path: '/cockpit-co', label: 'Cockpit', icon: <NavIcons.streams className={NAV_ICON_CLASS} />, roles: ['PILOT'], pilotMode: 'CO_PILOT', live: drohneLive },
    // Missionsplaner: Routen und Suchmuster planen, als KMZ für DJI Pilot 2 (nur Co-Pilot-Modus)
    { path: '/missionsplaner-co', label: 'Missionsplaner', icon: <NavIcons.route className={NAV_ICON_CLASS} />, roles: ['PILOT'], pilotMode: 'CO_PILOT' },
  ]

  const userMenu: UserMenuEntry[] = useMemo(() => [
    { id: 'settings', label: 'Einstellungen', icon: UserMenuIcons.settings, to: '/settings' },
    {
      id: 'feedback', label: 'Feedback & Roadmap', icon: UserMenuIcons.feedback, to: '/feedback',
      state: { von: location.pathname + location.search }, badge: feedbackUngelesen,
    },
    { type: 'heading', label: 'Handbuch' },
    { id: 'hb-kompakt', label: 'Handbuch kompakt', icon: UserMenuIcons.handbookShort, href: '/handbuch/anwenderhandbuch-pilot-kompakt.pdf' },
    { id: 'hb-lang', label: 'Handbuch ausführlich', icon: UserMenuIcons.handbookLong, href: '/handbuch/anwenderhandbuch-pilot-ausfuehrlich.pdf' },
    { type: 'separator' },
    { id: 'logout', label: 'Abmelden', icon: UserMenuIcons.logout, danger: true, onSelect: () => logout() },
  ], [location.pathname, location.search, feedbackUngelesen, logout])

  // Seiten, die einen Einsatz brauchen (Chip in Warnfarbe ohne Wahl)
  const braucht = location.pathname === '/taktik-karte-co' || location.pathname === '/checklisten'

  const modusKnopf = (modus: PilotMode, label: string, Icon: typeof Plane) => (
    <button
      key={modus}
      data-rolle={modus}
      onClick={() => setPilotMode(modus)}
      title={`${label}-Modus`} aria-label={`${label}-Modus`} aria-pressed={pilotMode === modus}
      className={`rolle-knopf flex min-h-[48px] min-w-[48px] items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-medium transition-colors sm:px-3 ${
        pilotMode === modus ? '' : 'text-dark-text-muted/60 hover:bg-dark-bg-tertiary hover:text-dark-text-muted'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="hidden whitespace-nowrap lg:inline">{label}</span>
      {pilotMode === modus && <Check className="ml-1 hidden h-3.5 w-3.5 lg:block" />}
    </button>
  )

  return (
    <AppShell
      moduleName="Piloten-Modul"
      moduleSubtitle="ASB Drohnenstaffel"
      logoSrc="/logo.png"
      navItems={navItems}
      pilotMode={pilotMode}
      dockPaths={['/flug-erfassen', '/flugbuch', '/checklisten', '/taktik-karte-co', '/cockpit-co', '/missionsplaner-co']}
      dockLabels={{ '/flug-erfassen': 'Flug', '/checklisten': 'Dashboard', '/taktik-karte-co': 'Karte' }}
      pageTitles={PAGE_TITLES}
      einsatzWahl={<EinsatzWahl braucht={braucht} hideWhenSingle={pilotMode === 'CO_PILOT'} />}
      status={<StatusBar badge={queueAnzahl} badgeLabel={`${queueAnzahl} ${queueAnzahl === 1 ? 'Flug-Aktion' : 'Flug-Aktionen'} offline gespeichert`} />}
      banner={<><StaleBanner /><FlugQueueHinweis /><EinsatzHinweis /><FlugStatusChip variant="streifen" /></>}
      user={{ name: `${user?.vorname ?? ''} ${user?.nachname ?? ''}`.trim(), username: user?.benutzername, subtitle: (user?.rollen?.[0] as string | undefined), dot: feedbackUngelesen > 0 }}
      userMenu={userMenu}
      headerRight={
        <>
          <FlugStatusChip variant="kopf" />
          {/* Handy: Segment „P | CP“ (Rollenfarben Cyan/Violett), Kurztext statt reinem Symbol */}
          <div className="flex shrink-0 items-center gap-0.5 rounded-full bg-dark-bg-tertiary/70 p-0.5 sm:hidden" role="group" aria-label="Pilot-Modus">
            {([['PILOT', 'P', 'Pilot'], ['CO_PILOT', 'CP', 'Co-Pilot']] as const).map(([m, kurz, voll]) => (
              <button
                key={m}
                type="button"
                data-rolle={m}
                aria-pressed={pilotMode === m}
                onClick={() => setPilotMode(m)}
                title={`${voll}-Modus`}
                aria-label={`${voll}-Modus${pilotMode === m ? ' aktiv' : ''}`}
                className={`rolle-knopf flex h-11 min-w-[40px] items-center justify-center rounded-full px-1.5 text-sm font-bold ${pilotMode === m ? '' : 'text-dark-text-muted/60'}`}
              >
                {kurz}
              </button>
            ))}
          </div>
          <div className="hidden items-center gap-1 rounded-full bg-dark-bg-tertiary/70 p-0.5 sm:flex" role="group" aria-label="Pilot-Modus">
            {modusKnopf('PILOT', 'Pilot', Plane)}
            {modusKnopf('CO_PILOT', 'Co-Pilot', UserCog)}
          </div>
        </>
      }
      contentTop={<><CloudAufforderungen pilotMode={pilotMode} /><DjiZuordnung /><VorflugPopup pilotMode={pilotMode} /></>}
    />
  )
}
